import React, { memo, useState, useCallback, useRef } from "react";
import { Card, Button, Modal } from "antd";
import { Redirect, useParams } from "react-router-dom";
import { ExportOutlined, UserAddOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { getApi, deleteApi } from "utils/apiCall";
import StaffDirectory from "./StaffDirectory";
import AddStaffModal from "./AddStaffModal";
import EditStaffModal from "./EditStaffModal";
import ViewStaffModal from "./ViewStaffModal";
import ComplianceTab from "./ComplianceTab";
import DeletedUsersTab from "./DeletedUsersTab";

const { confirm } = Modal;

const FILE_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

// Asepsis apps show dates as DD-MM-YYYY.
const toDdMmYyyy = (d) => `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return toDdMmYyyy(d);
};

const docStatus = (doc) => {
  if (!doc || doc.status !== "uploaded") return "Missing";
  return "Uploaded";
};

const fluCovidStatus = (doc) => {
  if (!doc || doc.status !== "uploaded") return "Missing";
  return doc.vaccination_date
    ? `Uploaded (Vaccinated: ${formatDate(doc.vaccination_date)})`
    : "Uploaded";
};

const policeStatus = (doc) => {
  if (!doc || doc.status !== "uploaded") return "Missing";
  return doc.issue_date
    ? `Uploaded (Issued: ${formatDate(doc.issue_date)})`
    : "Uploaded";
};

const writeExcel = (rows, fileName) => {
  if (!rows.length) { toast.error("There is no data to export."); return; }
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  FileSaver.saveAs(new Blob([buffer], { type: FILE_TYPE }), `${fileName}.xlsx`);
};

const HRUsers = () => {
  const [addStaffVisible, setAddStaffVisible]   = useState(false);
  const [editStaffVisible, setEditStaffVisible] = useState(false);
  const [editStaffRecord, setEditStaffRecord]   = useState(null);
  const [viewStaffVisible, setViewStaffVisible] = useState(false);
  const [viewStaffId, setViewStaffId]           = useState(null);
  const [directoryRefreshKey, setDirectoryRefreshKey] = useState(0);
  // Staff Directory / Compliance / Deleted Users are header tabs (routes);
  // see NavigationConfig's "workforce" entry.
  const { tab } = useParams();
  const activeTab = tab;
  const [exporting, setExporting]               = useState(false);

  // Refs to hold current active filters from child tabs
  const directoryFiltersRef  = useRef({});
  const complianceFiltersRef = useRef({});

  const handleEditStaff = useCallback((record) => {
    setEditStaffRecord(record);
    setEditStaffVisible(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditStaffVisible(false);
    setEditStaffRecord(null);
  }, []);

  const handleViewStaff = useCallback((record) => {
    setViewStaffId(record.staffId);
    setViewStaffVisible(true);
  }, []);

  const handleViewClose = useCallback(() => {
    setViewStaffVisible(false);
    setViewStaffId(null);
  }, []);

  const handleRefresh = useCallback(() => {
    setDirectoryRefreshKey((prev) => prev + 1);
  }, []);

  const handleDeleteStaff = useCallback((record) => {
    confirm({
      title: "Delete Staff",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete ${record.name}? They can be reactivated from Deleted Staff.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const { error, result } = await deleteApi(`/delete_org_user_details/${record.staffId}`);

          if (error) {
            toast.error(result?.message || "The staff member could not be deleted. Please try again.");
          } else {
            toast.success("Staff member deleted successfully.");
            handleRefresh();
          }
        } catch (err) {
          console.error("Error deleting staff:", err);
          toast.error("The staff member could not be deleted. Please try again.");
        }
      },
    });
  }, [handleRefresh]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      // Build query from current active filters
      const filters = activeTab === "staff-directory"
        ? directoryFiltersRef.current
        : complianceFiltersRef.current;

      const params = new URLSearchParams();
      if (filters.name)       params.set("name", filters.name);
      if (filters.role)       params.set("role", filters.role);
      if (filters.facilityId) params.set("facility_id", String(filters.facilityId));
      if (filters.groupId)    params.set("org_id", String(filters.groupId));
      if (filters.status)     params.set("status", filters.status);

      const query = params.toString();
      const { error, result } = await getApi(`/api/staff/export${query ? `?${query}` : ""}`);

      if (error || !result?.result) {
        toast.error("Export failed. Please try again.");
        return;
      }

      const items = Array.isArray(result.result) ? result.result : [];
      if (!items.length) { toast.error("There is no data to export."); return; }

      if (activeTab === "staff-directory") {
        const rows = items.map((s) => ({
          "Name":              s.full_name || "",
          "Group":      s.organization_name || "",
          "Facility":          (s.facilities || []).map((f) => f.facility_name).join(", "),
          "Role":              s.role || "",
          "Compliance Status": s.overall_status || "",
          "Flu Vaccination":   fluCovidStatus(s.flu_vaccination),
          "COVID Vaccination": fluCovidStatus(s.covid_vaccination),
          "Stat Declaration":  docStatus(s.statutory_declaration),
          "Police Check":      policeStatus(s.police_check),
        }));
        writeExcel(rows, "Staff_Directory");
      } else {
        const rows = items.map((s) => ({
          "Staff Name":        s.full_name || "",
          "Group":      s.organization_name || "",
          "Facility":          (s.facilities || []).map((f) => f.facility_name).join(", "),
          "Flu":               fluCovidStatus(s.flu_vaccination),
          "Covid":             fluCovidStatus(s.covid_vaccination),
          "Stat Declaration":  docStatus(s.statutory_declaration),
          "Police Check":      policeStatus(s.police_check),
          "Overall Status":    s.overall_status || "",
        }));
        writeExcel(rows, "Compliance");
      }
    } catch (err) {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [activeTab]);

  if (!["staff-directory", "compliance", "deleted-users"].includes(activeTab)) {
    return <Redirect to="/app/hr-users/staff-directory" />;
  }

  return (
    <Card key={activeTab} bodyStyle={{ padding: "24px 28px" }}>
      {/* Export and Add Staff belong to the active staff, not the deleted list */}
      {activeTab !== "deleted-users" && (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
          <Button
            icon={<ExportOutlined />}
            onClick={handleExport}
            loading={exporting}
          >
            Export
          </Button>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            style={{ background: "#2f6fe0", borderColor: "#2f6fe0" }}
            onClick={() => setAddStaffVisible(true)}
          >
            Add Staff
          </Button>
        </div>
      </div>
      )}

      <AddStaffModal
        visible={addStaffVisible}
        onClose={() => setAddStaffVisible(false)}
        onCreated={handleRefresh}
      />
      <EditStaffModal
        visible={editStaffVisible}
        staffId={editStaffRecord?.staffId}
        onClose={handleEditClose}
        onUpdated={handleRefresh}
      />
      <ViewStaffModal
        visible={viewStaffVisible}
        staffId={viewStaffId}
        onClose={handleViewClose}
      />

      {activeTab === "staff-directory" ? (
        <StaffDirectory
          onEdit={handleEditStaff}
          onView={handleViewStaff}
          onDelete={handleDeleteStaff}
          refreshKey={directoryRefreshKey}
          onDataChange={(list) => {}}
          onFiltersChange={(filters) => { directoryFiltersRef.current = filters; }}
        />
      ) : activeTab === "compliance" ? (
        <ComplianceTab
          onDataChange={(list) => {}}
          onFiltersChange={(filters) => { complianceFiltersRef.current = filters; }}
        />
      ) : (
        <DeletedUsersTab refreshKey={directoryRefreshKey} />
      )}
    </Card>
  );
};

export default memo(HRUsers);
